import React, { useState } from 'react';
import { getVehicleBrand, formatVehicleBrandModel } from '../../../utils/vehicleBrand';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Collapse,
  Divider,
  Heading,
  Text,
  UnorderedList,
  ListItem,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, DownloadIcon } from '@chakra-ui/icons';
import { getCollapseHeaderStyles } from './styles';

const VehicleDetails = ({ vehicles, onAddService }) => {
  const [showVehicleDetails, setShowVehicleDetails] = useState(null);
  const [showServiceDetails, setShowServiceDetails] = useState(null);

  let inputColor = useColorModeValue('gray.500', 'gray.400');

  const handlePrintInvoice = (invoice) => {
    if (!invoice) return;
    const invoiceContent = `
      <div>
        <h1>Factura: ${invoice.series || ''}-${invoice.number ?? ''}</h1>
        <p>Fecha: ${invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString() : 'N/A'}</p>
      </div>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Box mt="6">
      {vehicles && vehicles.length > 0 ? (
        vehicles.map((vehicle) => (
          <Box
            key={vehicle.id}
            borderWidth="1px"
            borderRadius="lg"
            p="6"
            mb="4"
          >
            {/* Header del Vehículo */}
            <Flex
              justify="space-between"
              align="center"
              sx={getCollapseHeaderStyles(showVehicleDetails === vehicle.id)}
              onClick={() =>
                setShowVehicleDetails((prev) =>
                  prev === vehicle.id ? null : vehicle.id,
                )
              }
              cursor="pointer"
            >
              <Heading size="md">
                {formatVehicleBrandModel(vehicle)} ({vehicle.year})
              </Heading>
              <Text>
                <strong>Chasis:</strong> {vehicle.chassisNumber}
              </Text>
              <Text>
                <strong>Matrícula:</strong> {vehicle.plate}
              </Text>
              <IconButton
                aria-label={
                  showVehicleDetails === vehicle.id
                    ? 'Ocultar detalles'
                    : 'Mostrar detalles'
                }
                icon={
                  showVehicleDetails === vehicle.id ? (
                    <ChevronUpIcon />
                  ) : (
                    <ChevronDownIcon />
                  )
                }
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVehicleDetails((prev) =>
                    prev === vehicle.id ? null : vehicle.id,
                  );
                }}
              />
            </Flex>

            {/* Detalles del Vehículo */}
            <Collapse in={showVehicleDetails === vehicle.id}>
              <Flex wrap="wrap" mt="4" gap={4}>
                {[
                  { label: 'Matrícula', value: vehicle.plate },
                  { label: 'Chasis', value: vehicle.chassisNumber },
                  { label: 'Motor', value: vehicle.engineNumber },
                  { label: 'Marca', value: getVehicleBrand(vehicle) },
                  { label: 'Modelo', value: vehicle.model?.description || 'N/A' },
                  { label: 'Año', value: vehicle.year },
                  { label: 'Color', value: vehicle.color?.name || 'N/A' },
                  {
                    label: 'Tipo de combustible',
                    value: vehicle.fuelType?.type || 'N/A',
                  },
                  {
                    label: 'Último Registro de Kilómetros',
                    value: `${vehicle.kilometers} km`,
                  },
                ].map((field, index) => (
                  <FormControl
                    key={index}
                    width={{ base: '100%', md: 'calc(50% - 16px)' }}
                    mb="2"
                  >
                    <FormLabel fontWeight="bold">{field.label}</FormLabel>
                    <Input value={field.value} isReadOnly color={inputColor} />
                  </FormControl>
                ))}
              </Flex>

              <Divider my="4" />

              {/* Servicios Realizados */}
              <Flex justify="space-between" align="center" mb="4">
                <Heading size="md">Servicios Realizados</Heading>
                <Button
                  colorScheme="gray"
                  onClick={() => onAddService(vehicle.id)}
                >
                  Nuevo Servicio
                </Button>
              </Flex>

              {vehicle.vehicleServices && vehicle.vehicleServices.length > 0 ? (
                vehicle.vehicleServices.map((service) => (
                  <Box key={service.id} mb="3">
                    <Flex
                      justify="left"
                      align="center"
                      sx={getCollapseHeaderStyles(
                        showServiceDetails === service.id,
                      )}
                      onClick={() =>
                        setShowServiceDetails((prev) =>
                          prev === service.id ? null : service.id,
                        )
                      }
                      cursor="pointer"
                    >
                      <Text fontWeight="bold" marginRight={3}>
                        {service.startDate}
                      </Text>
                      <Text>{service.description}</Text>
                      <IconButton
                        aria-label={
                          showServiceDetails === service.id
                            ? 'Ocultar detalles del servicio'
                            : 'Mostrar detalles del servicio'
                        }
                        icon={
                          showServiceDetails === service.id ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        }
                        variant="ghost"
                      />
                    </Flex>

                    {/* Detalles del Servicio */}
                    <Collapse in={showServiceDetails === service.id}>
                      <Flex wrap="wrap" mt="3" pl="4" gap={4}>
                        {[
                          {
                            label: 'Fecha',
                            value: service.date
                              ? new Date(service.date).toLocaleDateString()
                              : 'N/A',
                          },
                          { label: 'Estado', value: service.status?.description || 'N/A' },
                          {
                            label: 'Observaciones',
                            value: service.observations || 'N/A',
                          },
                          {
                            label: 'Factura',
                            value: service.invoice
                              ? `${service.invoice.series || ''}-${service.invoice.number ?? ''}`
                              : 'Sin facturar',
                          },
                          {
                            label: 'Fecha factura',
                            value: service.invoice?.issueDate
                              ? new Date(service.invoice.issueDate).toLocaleDateString()
                              : 'N/A',
                          },
                        ].map((field, index) => (
                          <FormControl
                            key={index}
                            width={{ base: '100%', md: 'calc(50% - 16px)' }}
                            mb="2"
                          >
                            <FormLabel fontWeight="bold">
                              {field.label}
                            </FormLabel>
                            <Input
                              value={field.value}
                              isReadOnly
                              color={inputColor}
                            />
                          </FormControl>
                        ))}
                      </Flex>
                      <Flex justify="space-between" mt="4">
                        <Box width="48%">
                          <Heading size="sm" mb="2">
                            Servicios Realizados
                          </Heading>
                          <UnorderedList pl="0">
                            {service.vehicleServiceEventsHistories?.map((event) => (
                              <ListItem key={event.id} mb="4">
                                <Text fontWeight="semibold">
                                  {event.eventName || event.event?.name || 'Evento'}
                                </Text>
                                <Text
                                  fontSize="0.9rem"
                                  color={event.completed ? 'green.600' : 'orange.600'}
                                >
                                  {event.completed ? 'Completado' : 'Pendiente'}
                                </Text>
                                {event.comment && (
                                  <Text fontSize="0.9rem" color="gray.600">
                                    {event.comment}
                                  </Text>
                                )}
                              </ListItem>
                            ))}
                          </UnorderedList>
                        </Box>

                        <Box width="48%">
                          <Heading size="sm" mb="2">
                            Productos Utilizados
                          </Heading>
                          <UnorderedList width="100%" pl="0">
                            {service.usedProducts?.map((product) => (
                              <ListItem key={product.id} mb="2">
                                <Flex justify="left">
                                  <Text marginRight={3}>
                                    <strong>Producto:</strong>{' '}
                                    {product.productName}
                                  </Text>
                                  <Text>
                                    <strong>Cantidad:</strong>{' '}
                                    {product.quantity} {product.product?.unit?.name || ''}
                                  </Text>
                                </Flex>
                              </ListItem>
                            ))}
                          </UnorderedList>
                        </Box>
                      </Flex>
                      {/* Imprimir Factura Button */}
                      {service.invoice && (
                        <Flex align="center" mt="5">
                          <Button
                            leftIcon={<DownloadIcon />}
                            colorScheme="gray"
                            onClick={() => handlePrintInvoice(service.invoice)}
                          >
                            Descargar Factura
                          </Button>
                        </Flex>
                      )}
                    </Collapse>
                  </Box>
                ))
              ) : (
                <Text>No hay servicios registrados.</Text>
              )}
            </Collapse>
          </Box>
        ))
      ) : (
        <Text>No hay vehículos disponibles.</Text>
      )}
    </Box>
  );
};

export default VehicleDetails;
